/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { defineMessages } from 'react-intl';
import { FormFieldValue, FormValidator } from 'src/core/types/form';
import * as booleanValidators from 'src/core/utils/formValidators/booleanValidators';
import * as fileValidators from 'src/core/utils/formValidators/fileValidators';
import * as numberValidators from 'src/core/utils/formValidators/numberValidators';
import * as stringArrayValidators from 'src/core/utils/formValidators/stringArrayValidators';
import * as stringValidators from 'src/core/utils/formValidators/stringValidators';

const messages = defineMessages({
    errorMessage: {
        id: 'portal.utils.createDuplicateValueValidator',
        defaultMessage: "Invalid extension. Extension required is '{extension}'"
    }
});

/**
 * A factory to create an extension validator. An extension validator will succeed
 * when the given value has the required extension.
 *
 * @param extension A string of the extension required. Ex: 'txt', 'json', etc. The
 * extension should not be prefixed with a '.', only the string part of the extension
 * should be passed.
 */
export const createExtensionValidator = (extension: string): FormValidator => (value: FormFieldValue) => {
    let validationResult: boolean;

    if (!extension) {
        return;
    }

    if (typeof value === 'number') {
        validationResult = numberValidators.createExtensionValidator(extension)(value);
    }
    if (typeof value === 'boolean') {
        validationResult = booleanValidators.createExtensionValidator(extension)(value);
    }
    if (typeof value === 'string') {
        validationResult = stringValidators.createExtensionValidator(extension)(value);
    }
    if (value instanceof File) {
        validationResult = fileValidators.createExtensionValidator(extension)(value);
    }
    if (Array.isArray(value)) {
        validationResult = stringArrayValidators.createExtensionValidator(extension)(value);
    }

    if (!validationResult) {
        return { messageDescriptor: messages.errorMessage, params: { extension } };
    }
};
